/**
 * PUBLICATION → GRID detail overlay (React/SVG).
 *
 * Renders ACTIVE publication snapshots on the public Grid at the DETAIL LOD
 * ONLY — i.e. once the camera has zoomed IN past the LOD0 overview cutoff
 * (`LOD0_VISIBILITY_MAX_ZOOM = 4.27`), the exact zoom band where the coarse
 * Brand Image overview disappears. This reuses the existing, frozen Canvas V1
 * public rendering semantics (the same read-only element model the standalone
 * `#p/<id>` surface serves) rather than inventing a second Canvas renderer or a
 * parallel publication authority. Content is positioned in WORLD coordinates via
 * the canonical camera math and clipped to the EXACT parcel union (never a
 * bbox), so it stays visually bound to its Territory through expansion.
 *
 * Interaction law (§9): the overlay is pointer-transparent everywhere EXCEPT on
 * real link/social anchors, so pan/zoom/selection pass straight through to the
 * Grid canvas; a deliberate anchor click opens the URL and never triggers a pan.
 */
import { memo } from "react";

import { hexOf, FONT_STACKS, socialStyleWithDefaults, contactsStyleWithDefaults, linkStyleWithDefaults, type ContactEntry } from "../domain/canvas-model";
import { publicationMediaContentUrl } from "../lib/publication-api";
import type { Camera } from "./camera";
import { worldToScreen } from "./camera";
import { AutoFitText } from "./AutoFitText";
import { SocialRender } from "./SocialRender";
import { ContactsRender } from "./ContactsRender";
import { PdfCard } from "./PdfCard";
import { FitToBox } from "./FitToBox";
import { LinkRender } from "./LinkRender";
import { PixelArtRender } from "./PixelArtRender";
import { unionScreenRects, unionBoundsWorld, type UnitRect } from "./brand-image-geometry";
import { useContentAnchorProps } from "./contentPointer";
import type { PublicationRenderable } from "./usePublicationDiscovery";

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface PubElement {
  type: string;
  element_id?: string;
  z?: number;
  hidden?: boolean;
  opacity?: number;
  rotation?: number;
  box?: Box;
  line?: { x1: number; y1: number; x2: number; y2: number };
  text?: string;
  asset_id?: string;
  fill_color?: string;
  line_color?: string;
  color?: string;
  shape_subtype?: string;
  radius?: string;
  url?: string;
  label?: string;
  subtitle?: string;
  link_style?: Record<string, unknown>;
  title?: string;
  text_style?: Record<string, unknown>;
  present?: Record<string, unknown>;
  destinations?: Array<{ platform?: string; url?: string; label?: string; icon_color?: string }>;
  entries?: ContactEntry[];
  social_style?: Record<string, unknown>;
  contacts_style?: Record<string, unknown>;
  pdf_bg_asset_id?: string;
  pixel_art?: { w: number; h: number; palette: string[]; rle: [number, number][] };
}

const GLOW_NONE = "none";
// Perimeter glow px per authored level — mirrors the private editor semantics so
// the published Territory border reads identically on the Grid.
const GLOW_PX: Record<string, number> = { OFF: 0, LOW: 6, MEDIUM: 14, HIGH: 26 };

function isHttps(u: unknown): u is string {
  return typeof u === "string" && u.startsWith("https://");
}

function ElementView({
  el,
  identityId,
  revision,
  camera,
}: {
  el: PubElement;
  identityId: string;
  revision: number;
  camera: Camera;
}) {
  const anchorProps = useContentAnchorProps();
  if (el.hidden) return null;
  const scale = camera.scale;
  const opacity = typeof el.opacity === "number" ? el.opacity : 1;

  if (el.line) {
    const a = worldToScreen(camera, el.line.x1, el.line.y1);
    const b = worldToScreen(camera, el.line.x2, el.line.y2);
    return (
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={hexOf(el.line_color, "#f5f7fa")}
        strokeWidth={2.5}
        opacity={opacity}
        strokeLinecap="round"
      />
    );
  }
  if (!el.box) return null;
  const s = worldToScreen(camera, el.box.x, el.box.y);
  const w = Math.max(1, el.box.w * scale);
  const h = Math.max(1, el.box.h * scale);
  const cx = s.x + w / 2;
  const cy = s.y + h / 2;
  const transform = el.rotation ? `rotate(${el.rotation} ${cx} ${cy})` : undefined;
  const rx = el.radius === "PILL" ? h / 2 : el.radius === "ROUNDED" ? 10 : el.radius === "SLIGHT" ? 4 : 0;
  const common = { transform, opacity, style: { filter: GLOW_NONE } as React.CSSProperties };

  if (el.type === "SHAPE" && el.shape_subtype === "ELLIPSE") {
    return <ellipse cx={cx} cy={cy} rx={w / 2} ry={h / 2} fill={hexOf(el.fill_color, "#8a97a8")} {...common} />;
  }
  if (el.type === "SHAPE") {
    return <rect x={s.x} y={s.y} width={w} height={h} rx={rx} fill={hexOf(el.fill_color, "#8a97a8")} {...common} />;
  }
  if (el.type === "IMAGE" || el.type === "GIF") {
    if (!el.asset_id) return null;
    const fit = String(el.present?.fit ?? "COVER") === "CONTAIN" ? "contain" : "cover";
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: rx, background: "#0d1117" }}>
          <img
            data-testid="grid-pub-image"
            src={publicationMediaContentUrl(identityId, el.asset_id, revision)}
            alt=""
            draggable={false}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
            style={{ width: "100%", height: "100%", objectFit: fit as "cover" | "contain", display: "block", pointerEvents: "none" }}
          />
        </div>
      </foreignObject>
    );
  }
  if (el.type === "SOCIAL") {
    const ss = socialStyleWithDefaults(el.social_style);
    const ax = ss.align === "START" ? "flex-start" : ss.align === "END" ? "flex-end" : "center";
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        <FitToBox boxW={w} boxH={h} alignX={ax}>
          <SocialRender
            destinations={(el.destinations ?? []).map((d) => ({ platform: d.platform ?? "website", url: d.url ?? "", label: d.label, icon_color: d.icon_color }))}
            style={ss}
            scale={1}
            interactive
          />
        </FitToBox>
      </foreignObject>
    );
  }
  if (el.type === "CONTACTS") {
    const cs = contactsStyleWithDefaults(el.contacts_style);
    const ax = cs.align === "START" ? "flex-start" : cs.align === "END" ? "flex-end" : "center";
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        <FitToBox boxW={w} boxH={h} alignX={ax}>
          <ContactsRender entries={(el.entries ?? []) as ContactEntry[]} style={cs} scale={1} interactive />
        </FitToBox>
      </foreignObject>
    );
  }
  if (el.type === "PIXEL_ART" && el.pixel_art) {
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        <PixelArtRender pixelArt={el.pixel_art} boxW={w} boxH={h} />
      </foreignObject>
    );
  }
  if (el.type === "LINK") {
    const ls = linkStyleWithDefaults(el.link_style);
    const ax = ls.align === "START" ? "flex-start" : ls.align === "END" ? "flex-end" : "center";
    const block = (
      <FitToBox boxW={w} boxH={h} alignX={ax}>
        <LinkRender label={el.label ?? ""} subtitle={el.subtitle} url={el.url} style={ls} scale={1} interactive />
      </FitToBox>
    );
    if (isHttps(el.url)) {
      return (
        <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
          <a data-testid="grid-pub-link" href={el.url} target="_blank" rel="noopener noreferrer" {...anchorProps} style={{ display: "block", width: "100%", height: "100%", pointerEvents: "auto", textDecoration: "none" }}>
            {block}
          </a>
        </foreignObject>
      );
    }
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        {block}
      </foreignObject>
    );
  }
  // Text-bearing modules (TITLE/TAGLINE/DESCRIPTION), interactive (LINK/SOCIAL/
  // CONTACTS) and document cards (PDF/VIDEO) ALL render through the SAME shared
  // AutoFitText used by the private editor, so the Grid is PIXEL-IDENTICAL to
  // the Canvas (same font sizing, wrapping, alignment — no separate algorithm).
  const ts = (el.text_style ?? {}) as Record<string, unknown>;
  const font = FONT_STACKS[(ts.font as string) ?? "GRID_SANS"] ?? FONT_STACKS.GRID_SANS;
  const isDoc = el.type === "PDF" || el.type === "VIDEO";
  const label =
    el.type === "LINK" ? (el.label ?? "")
    : el.type === "SOCIAL" ? ((el.destinations ?? []).map((d) => d.platform).filter(Boolean).join(" · "))
    : el.type === "CONTACTS" ? ((el.entries ?? []).map((c) => c.label).filter(Boolean).join(" · "))
    : el.type === "PDF" ? `PDF${el.title ? ` · ${el.title}` : ""}`
    : el.type === "VIDEO" ? "▶ VIDEO"
    : (el.text ?? "");
  const color = isDoc ? "#8a97a8"
    : hexOf((ts.color as string) ?? (el.type === "LINK" ? el.color : ""), el.type === "LINK" ? "#2f7bff" : "#f5f7fa");
  const align = isDoc ? "CENTER" : ((ts.align as string) ?? "LEFT");
  const fit = (
    <AutoFitText
      text={String(label)}
      maxFontPx={isDoc ? h * 0.9 : ((ts.font_size as number) ?? 16) * scale}
      font={isDoc ? FONT_STACKS.GRID_SANS : font}
      color={color}
      bold={!isDoc && Boolean(ts.bold)}
      italic={!isDoc && Boolean(ts.italic)}
      underline={!isDoc && Boolean(ts.underline)}
      align={align}
      lineHeight={isDoc ? 1.15 : ((ts.line_height as number) ?? 1.2)}
    />
  );
  if (isDoc) {
    const docInner = (
      <div data-testid="grid-pub-doc" style={{ width: "100%", height: "100%", boxSizing: "border-box", borderRadius: rx, background: el.type === "PDF" ? "#1a2130" : "#141b28", border: "1px solid #2b3340", overflow: "hidden" }}>
        {fit}
      </div>
    );
    // A published PDF opens its document in a new tab (mirrors LINK anchors);
    // pointer stop-propagation so the click never triggers a grid pan.
    if (el.type === "PDF") {
      const bgUrl = el.pdf_bg_asset_id ? publicationMediaContentUrl(identityId, el.pdf_bg_asset_id, revision) : undefined;
      const card = <div data-testid="grid-pub-doc" style={{ width: "100%", height: "100%" }}><FitToBox boxW={w} boxH={h}><PdfCard title={el.title} scale={1} interactive bgImageUrl={bgUrl} /></FitToBox></div>;
      if (el.asset_id) {
        const href = publicationMediaContentUrl(identityId, el.asset_id, revision);
        return (
          <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
            <a data-testid="grid-pub-pdf-link" href={href} target="_blank" rel="noopener noreferrer"
              {...anchorProps}
              style={{ display: "block", width: "100%", height: "100%", pointerEvents: "auto", textDecoration: "none" }}>
              {card}
            </a>
          </foreignObject>
        );
      }
      return (
        <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
          {card}
        </foreignObject>
      );
    }
    return (
      <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
        {docInner}
      </foreignObject>
    );
  }
  return (
    <foreignObject x={s.x} y={s.y} width={w} height={h} {...common}>
      <div data-testid="grid-pub-text" style={{ width: "100%", height: "100%" }}>{fit}</div>
    </foreignObject>
  );
}

const PublicationGroup = memo(function PublicationGroup({
  item,
  camera,
}: {
  item: PublicationRenderable;
  camera: Camera;
}) {
  const { renderable, snapshot } = item;
  const units: UnitRect[] = renderable.units;
  const clip = unionScreenRects(camera, units);
  const clipId = `pubclip-${renderable.identityId}`;
  const elements = (snapshot.elements as unknown as PubElement[]) ?? [];
  const ordered = [...elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  // Territory-level presentation from the immutable snapshot (background fill +
  // perimeter). These are authored on the Territory, not per element, so the
  // Grid must render them alongside the element content or the published look is
  // incomplete (solid background / border colour would silently disappear).
  const style = (snapshot.style ?? {}) as {
    background_mode?: string; background_color?: string;
    perimeter_color?: string; perimeter_glow?: string;
  };
  const bg = style.background_mode === "SOLID_COLOR" ? hexOf(style.background_color, "#0d1117") : null;
  const perim = hexOf(style.perimeter_color, "#22d3ee");
  const glowPx = GLOW_PX[style.perimeter_glow ?? "OFF"] ?? 0;
  const wb = unionBoundsWorld(units);
  const tl = wb ? worldToScreen(camera, wb.x, wb.y) : null;
  const frame = wb && tl ? { x: tl.x, y: tl.y, w: wb.w * camera.scale, h: wb.h * camera.scale } : null;
  return (
    <g data-testid={`grid-pub-${renderable.identityId}`} data-revision={snapshot.publication_revision}>
      <defs>
        <clipPath id={clipId}>
          {clip.map((r, i) => (
            <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} />
          ))}
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {bg && frame && <rect x={frame.x} y={frame.y} width={frame.w} height={frame.h} fill={bg} />}
        {ordered.map((el, i) => (
          <ElementView
            key={el.element_id ?? i}
            el={el}
            identityId={renderable.identityId}
            revision={snapshot.publication_revision}
            camera={camera}
          />
        ))}
        {frame && (
          <rect
            data-testid="grid-pub-perimeter"
            x={frame.x}
            y={frame.y}
            width={frame.w}
            height={frame.h}
            fill="none"
            stroke={perim}
            strokeWidth={4}
            style={{ filter: glowPx ? `drop-shadow(0 0 ${glowPx}px ${perim})` : undefined }}
          />
        )}
      </g>
    </g>
  );
});

/**
 * Detail-LOD publication overlay. `camera` is non-null ONLY at the detail LOD
 * (the GridViewer publishes it past the LOD0 overview cutoff), so at coarse
 * zoom this renders nothing and the normal Grid representation is untouched.
 */
export const GridPublicationLayer = memo(function GridPublicationLayer({
  camera,
  items,
}: {
  camera: Camera | null;
  items: PublicationRenderable[];
}) {
  if (!camera || items.length === 0) return null;
  return (
    <svg
      data-testid="grid-publication-layer"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "hidden" }}
    >
      {items.map((item) => (
        <PublicationGroup key={item.renderable.identityId} item={item} camera={camera} />
      ))}
    </svg>
  );
});
